/** mortgage-svc routes — 1003 applications + the DTI worksheet (Phase 5.5/5.6). */

import type {
  ApplicationCreateRequest,
  ApplicationUpdateRequest,
  DtiCalculation,
  LoanInputs,
  MortgageApplication,
  OverrideCreateRequest,
  ScenarioSummary,
  UrlaSections,
  UrlaSectionsOut,
  Uuid,
} from "@re-platform/types";

import type { ApiClient } from "../client";

export const mortgageService = {
  // -- 1003 applications ------------------------------------------------------

  createApplication(client: ApiClient, body: ApplicationCreateRequest) {
    return client.request<MortgageApplication>("/mortgage/applications", {
      method: "POST",
      body,
    });
  },

  listApplications(
    client: ApiClient,
    params?: {
      client_id?: Uuid;
      status?: string;
      limit?: number;
      offset?: number;
    },
  ) {
    return client.request<MortgageApplication[]>("/mortgage/applications", {
      query: params,
    });
  },

  getApplication(client: ApiClient, applicationId: Uuid) {
    return client.request<MortgageApplication>(
      `/mortgage/applications/${applicationId}`,
    );
  },

  updateApplication(
    client: ApiClient,
    applicationId: Uuid,
    body: ApplicationUpdateRequest,
  ) {
    return client.request<MortgageApplication>(
      `/mortgage/applications/${applicationId}`,
      {
        method: "PATCH",
        body,
      },
    );
  },

  // -- DTI worksheet ----------------------------------------------------------

  getDti(client: ApiClient, applicationId: Uuid, scenario?: string) {
    return client.request<DtiCalculation>(
      `/mortgage/applications/${applicationId}/dti`,
      {
        query: scenario ? { scenario } : undefined,
      },
    );
  },

  calculateDti(
    client: ApiClient,
    applicationId: Uuid,
    body?: { scenario_name?: string; loan_inputs?: LoanInputs },
  ) {
    return client.request<DtiCalculation>(
      `/mortgage/applications/${applicationId}/dti/calculate`,
      { method: "POST", body: body ?? {} },
    );
  },

  /** Apply a manual override (reason mandatory — audited) and recalculate. */
  createOverride(client: ApiClient, body: OverrideCreateRequest) {
    return client.request<DtiCalculation>("/mortgage/dti/overrides", {
      method: "POST",
      body,
    });
  },

  /** Retire an active override (audited) and recalculate. */
  deleteOverride(client: ApiClient, overrideId: Uuid) {
    return client.request<DtiCalculation>(
      `/mortgage/dti/overrides/${overrideId}`,
      {
        method: "DELETE",
      },
    );
  },

  /** Save a named what-if scenario (never becomes is_current). */
  createScenario(
    client: ApiClient,
    body: {
      application_id: Uuid;
      scenario_name: string;
      loan_inputs?: LoanInputs;
    },
  ) {
    return client.request<DtiCalculation>("/mortgage/dti/scenarios", {
      method: "POST",
      body,
    });
  },

  listScenarios(client: ApiClient, applicationId: Uuid) {
    return client.request<ScenarioSummary[]>("/mortgage/dti/scenarios", {
      query: { application_id: applicationId },
    });
  },

  // -- The borrower's own 1003 sections ---------------------------------------

  /** The caller's own open application, opening one if they have none. Onboarding
   * creates a dossier and a deal but no application, so without this a borrower
   * had no id with which to reach the form they must fill in. */
  myApplication(client: ApiClient) {
    return client.request<MortgageApplication>("/mortgage/my-application");
  },

  /** Null when the borrower has not filled them in yet — including when stored
   * data no longer validates, so they can simply answer again. */
  getUrla(client: ApiClient, applicationId: Uuid) {
    return client.request<UrlaSectionsOut | null>(
      `/mortgage/applications/${applicationId}/urla`,
    );
  },

  /** Whole-section save, not a patch: the declarations are answered as a set and
   * signed as a set, so a partial save would look answered without being. */
  putUrla(client: ApiClient, applicationId: Uuid, body: UrlaSections) {
    return client.request<UrlaSectionsOut>(
      `/mortgage/applications/${applicationId}/urla`,
      { method: "PUT", body },
    );
  },
};
